import {
  Button,
  Dialog,
  Icon,
  useFullPageDialogContext,
  DialogPage,
  DialogContent,
  DialogCTA,
} from "@sellar-io/components";
import { ReactNode, useState } from "react";
import { capitalize } from "@sellar-io/common";
import { Alert } from "../../../../components/alert/Alert";
import { SellarDistributionTimePreferenceType } from "@sellar-io/api-v2/types";
import { useBuyerSellarDistributionMetaUpsertTimePreference } from "../../../../hooks/api/v2/retailerSellarDistributionMeta/useBuyerSellarDistributionMetaUpsertTimePreference";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { parse } from "postcode";
import {
  DistributionEducationDialogPage,
  deliveryTimePreferenceOptions,
  useGetDistributionDeliveryDays,
  listFormatter,
  useGetSupplierRelationshipDeliveryTimePreference,
  useGetDistributionDeliveryDrivers,
} from "./DistributionEducationDialog.shared";

type DistributionEducationDialogProps = {
  active: boolean;
  initialPage: DistributionEducationDialogPage;
  buyerPostcode: string;
  supplierId: number | null;
  onDismiss: () => void;
};

export const DistributionEducationDialog = ({
  active,
  initialPage,
  buyerPostcode,
  supplierId,
  onDismiss,
}: DistributionEducationDialogProps) => {
  const canSetupDeliveryPreferences = !!supplierId;
  return (
    <Dialog active={active} initPage={initialPage} onDismiss={onDismiss}>
      <DialogPage page={DistributionEducationDialogPage.EXPERT_DRAY_DELIVERY}>
        <ExpertDrayDeliveryEducationDialogContent
          buyerPostcode={buyerPostcode}
          onDismiss={onDismiss}
        />
      </DialogPage>
      <DialogPage page={DistributionEducationDialogPage.COMBINED_DELIVERIES}>
        <CombinedDeliveriesEducationDialogContent />
      </DialogPage>
      <DialogPage page={DistributionEducationDialogPage.FREE_TO_USE}>
        <FreeToUseEducationDialogContent />
      </DialogPage>
      <DialogPage page={DistributionEducationDialogPage.FLEXIBLE_SERVICE}>
        <FlexibleServiceEducationDialogContent
          onDismiss={onDismiss}
          canGoToDeliveryPreferences={canSetupDeliveryPreferences}
        />
      </DialogPage>
      {canSetupDeliveryPreferences && (
        <DialogPage
          page={DistributionEducationDialogPage.SET_DELIVERY_PREFERENCES}
        >
          <DeliveryPreferenceEducationDialogContent
            supplierId={supplierId}
            onDismiss={onDismiss}
          />
        </DialogPage>
      )}
    </Dialog>
  );
};

type CustomDialogContentProps = {
  image: ReactNode;
  title: string;
  subtitle: ReactNode;
  icon: ReactNode;
};
const CustomDialogContent = ({
  image,
  title,
  subtitle,
  icon,
}: CustomDialogContentProps) => {
  return (
    <div className="flex flex-col gap-4 p-6">
      {image}
      <div className="flex items-start gap-4">
        <div className="flex h-12 min-h-12 w-12 min-w-12 items-center justify-center rounded-lg bg-purple-50">
          {icon}
        </div>
        <div className="flex flex-col gap-1 text-zinc-600">
          <p className="text-lg font-semibold text-zinc-800">{title}</p>
          <div>{subtitle}</div>
        </div>
      </div>
    </div>
  );
};

export const ExpertDrayDeliveryEducationDialogContent = ({
  onDismiss,
  buyerPostcode,
}: {
  onDismiss: () => void;
  buyerPostcode: string;
}) => {
  const { setPage } = useFullPageDialogContext();
  const { drivers } = useGetDistributionDeliveryDrivers({ buyerPostcode });
  const { deliveryDays } = useGetDistributionDeliveryDays({ buyerPostcode });

  const formattedDeliveryDays = listFormatter.format(
    deliveryDays.map(capitalize),
  );

  const parsedPostcode = parse(buyerPostcode);

  const outcode = parsedPostcode.valid ? parsedPostcode.outcode : "";

  const title = "Expert dray delivery from our friendly team";

  const renderDriverAvatars = () => {
    return drivers
      .slice(0, 2)
      .map((driver) => (
        <img
          key={driver.id}
          src={driver.imageUrl ?? ""}
          className="h-12 w-12 rounded-full"
        />
      ));
  };

  const renderDriverNames = () => {
    return drivers
      .slice(0, 2)
      .map((driver) => {
        const [firstName, lastName] = driver.name.split(" ");
        return `${firstName} ${lastName ? `${lastName[0]}.` : ""}`;
      })
      .join(" & ");
  };

  return (
    <>
      <DialogContent className="p-0 md:h-110">
        <CustomDialogContent
          image={
            <div className="relative w-full rounded-lg md:h-64 md:max-h-64">
              <img
                src="https://sellar.imgix.net/sellar-image-assets/app-assets/distribution-education-landing-page.png"
                alt={title}
                className="h-full w-full"
              />
              <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-[#8C23C74D]">
                <div className="flex gap-3 rounded-xl border-2 border-[#BA7BDD] bg-white px-4 py-3">
                  <div className="flex items-center -space-x-4">
                    {renderDriverAvatars()}
                  </div>
                  <div className="flex flex-col font-sometype-mono">
                    <p className="font-bold uppercase">
                      Your {outcode} dray team
                    </p>
                    <p className="text-zinc-600">{renderDriverNames()}</p>
                  </div>
                </div>
              </div>
            </div>
          }
          title={title}
          subtitle={
            <div className="flex flex-col gap-2">
              <p>
                Since your postcode {buyerPostcode} sits within our delivery
                area, you qualify for expert dray delivery from our experienced
                drinks industry operators.
              </p>
              <p>
                Every week on <strong>{formattedDeliveryDays}</strong>
              </p>
            </div>
          }
          icon={
            <Icon icon="TRUCK_OUTLINE" size={1} className="text-[#8C23C7]" />
          }
        />
      </DialogContent>
      <DialogCTA>
        <Button onClick={onDismiss} color="transparent">
          Dismiss
        </Button>
        <Button
          onClick={() =>
            setPage(DistributionEducationDialogPage.COMBINED_DELIVERIES)
          }
        >
          Next
          <Icon icon="CHEVRON_RIGHT" />
        </Button>
      </DialogCTA>
    </>
  );
};

const CombinedDeliveriesEducationDialogContent = () => {
  const { setPage } = useFullPageDialogContext();

  const title = "Your orders delivered together";

  return (
    <>
      <DialogContent className="p-0 md:h-110">
        <CustomDialogContent
          image={
            <img
              src="https://sellar.imgix.net/sellar-image-assets/app-assets/distribution-education-combined-deliveries.png"
              alt={title}
              className="w-full rounded-lg md:h-64 md:max-h-64"
            />
          }
          title={title}
          subtitle={
            <p>
              Order from multiple participating suppliers. Get one delivery
              slot.
            </p>
          }
          icon={
            <Icon
              icon="PACKAGE_VARIANT_CLOSED_PLUS"
              size={1}
              className="text-[#8C23C7]"
            />
          }
        />
      </DialogContent>
      <DialogCTA>
        <Button
          onClick={() =>
            setPage(DistributionEducationDialogPage.EXPERT_DRAY_DELIVERY)
          }
          color="transparent"
        >
          <Icon icon="CHEVRON_LEFT" />
          Back
        </Button>
        <Button
          onClick={() => setPage(DistributionEducationDialogPage.FREE_TO_USE)}
        >
          Next
          <Icon icon="CHEVRON_RIGHT" />
        </Button>
      </DialogCTA>
    </>
  );
};

const FreeToUseEducationDialogContent = () => {
  const { setPage } = useFullPageDialogContext();

  const title = "Completely free to use";

  return (
    <>
      <DialogContent className="p-0 md:h-110">
        <CustomDialogContent
          image={
            <img
              src="https://sellar.imgix.net/sellar-image-assets/app-assets/distribution-education-free-to-use.png"
              alt={title}
              className="w-full rounded-lg md:h-64 md:max-h-64"
            />
          }
          title={title}
          subtitle={
            <p>No delivery charges, no minimums and no subscription fee.</p>
          }
          icon={
            <Icon icon="TAG_OFF_OUTLINE" size={1} className="text-[#8C23C7]" />
          }
        />
      </DialogContent>
      <DialogCTA>
        <Button
          onClick={() =>
            setPage(DistributionEducationDialogPage.COMBINED_DELIVERIES)
          }
          color="transparent"
        >
          <Icon icon="CHEVRON_LEFT" />
          Back
        </Button>
        <Button
          onClick={() =>
            setPage(DistributionEducationDialogPage.FLEXIBLE_SERVICE)
          }
        >
          Next
          <Icon icon="CHEVRON_RIGHT" />
        </Button>
      </DialogCTA>
    </>
  );
};

const FlexibleServiceEducationDialogContent = ({
  canGoToDeliveryPreferences,
  onDismiss,
}: {
  canGoToDeliveryPreferences: boolean;
  onDismiss: () => void;
}) => {
  const { setPage } = useFullPageDialogContext();

  const title = "Flexible service";

  return (
    <>
      <DialogContent className="p-0 md:h-110">
        <CustomDialogContent
          image={
            <img
              src="https://sellar.imgix.net/sellar-image-assets/app-assets/distribution-education-flexible-service.png"
              alt={title}
              className="w-full rounded-lg md:h-64 md:max-h-64"
            />
          }
          title={title}
          subtitle={<p>Set your preference of a AM or PM delivery time slot</p>}
          icon={
            <Icon
              icon="NOTE_EDIT_OUTLINE"
              className="text-[#8C23C7]"
              size={0.9}
            />
          }
        />
      </DialogContent>
      <DialogCTA>
        <Button
          onClick={() => setPage(DistributionEducationDialogPage.FREE_TO_USE)}
          color="transparent"
        >
          <Icon icon="CHEVRON_LEFT" />
          Back
        </Button>
        {canGoToDeliveryPreferences ? (
          <Button
            onClick={() =>
              setPage(DistributionEducationDialogPage.SET_DELIVERY_PREFERENCES)
            }
          >
            Set up Preferences
            <Icon icon="CHEVRON_RIGHT" />
          </Button>
        ) : (
          <Button onClick={onDismiss}>Complete</Button>
        )}
      </DialogCTA>
    </>
  );
};

const DeliveryPreferenceEducationDialogContent = ({
  supplierId,
  onDismiss,
}: {
  supplierId: number;
  onDismiss: () => void;
}) => {
  const [selectedPreference, setSelectedPreference] = useState<
    DeliveryTimePreferenceOption["value"] | null
  >(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { timePreference: initialTimePreference } =
    useGetSupplierRelationshipDeliveryTimePreference({
      supplierId,
    });
  const buyerSellarDistributionMetaUpsertTimePreferenceMutation =
    useBuyerSellarDistributionMetaUpsertTimePreference();

  const timePreference = selectedPreference || initialTimePreference;

  const handleSetPreference = () => {
    setErrorMessage(null);
    if (!timePreference) {
      setErrorMessage("Please select a delivery time preference");
      return;
    }

    buyerSellarDistributionMetaUpsertTimePreferenceMutation.mutate(
      {
        timePreference,
      },
      {
        onSuccess: onDismiss,
        onError: (error) => {
          const newErrorMessage =
            apiV2AssertError(error)?.response?.data.message ??
            "Something went wrong";
          setErrorMessage(newErrorMessage);
        },
      },
    );
  };

  const isUpdatingBuyerSdTimePreference =
    buyerSellarDistributionMetaUpsertTimePreferenceMutation.isPending;

  return (
    <>
      <DialogContent className="p-0 md:h-110">
        <div className="flex flex-col gap-6 p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-12 min-h-12 w-12 min-w-12 items-center justify-center rounded-lg bg-purple-50">
              <Icon icon="CLOCK_OUTLINE" size={1} className="text-[#8C23C7]" />
            </div>
            <div className="flex flex-col gap-1 text-zinc-600">
              <p className="text-lg font-semibold text-zinc-800">
                Choose your preferred delivery time slot
              </p>
              <div>
                Let us know your preferred time of day for deliveries so we can
                aim to meet your preference
              </div>
            </div>
          </div>
          {Boolean(errorMessage) && (
            <Alert message={errorMessage} type="error" className="w-full" />
          )}
          <div className="flex flex-col gap-2">
            {deliveryTimePreferenceOptions.map((option) => (
              <label
                className="flex cursor-pointer flex-col rounded-lg border border-zinc-300 bg-transparent p-4 has-checked:bg-zinc-100"
                htmlFor={option.value}
                key={option.value}
                onClick={() => setSelectedPreference(option.value)}
              >
                <span className="font-semibold">{option.label}</span>
                <span className="text-zinc-700">{option.description}</span>
                <input
                  type="radio"
                  hidden
                  disabled={isUpdatingBuyerSdTimePreference}
                  checked={timePreference === option.value}
                  readOnly
                  id={option.value}
                  name="delivery-preferences"
                />
              </label>
            ))}
          </div>
        </div>
      </DialogContent>
      <DialogCTA>
        <Button
          onClick={handleSetPreference}
          disabled={isUpdatingBuyerSdTimePreference}
          loading={isUpdatingBuyerSdTimePreference}
        >
          Save preferences
          <Icon icon="CHECK" />
        </Button>
      </DialogCTA>
    </>
  );
};

type DeliveryTimePreferenceOption = {
  label: string;
  description: string;
  value: SellarDistributionTimePreferenceType;
};
